// src/pages/head-of-operations/RiskRegisterPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../components/MasterSideBar.jsx";
import Topbar from "../../components/TopbarNav.jsx";
import { FiPlusCircle } from "react-icons/fi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// ✅ Import the modal and table
import RiskFormModal from "../../components/head-of-Operations/RiskFormModal.jsx";
import RiskTable from "../../components/head-of-Operations/RiskTable.jsx";
import EditRiskModal from "../../components/head-of-Operations/EditRiskModal.jsx";
import ViewRiskModal from "../../components/head-of-Operations/ViewRiskModal.jsx";
import BulkRiskUploadModal from "../../components/head-of-Operations/BulkRiskUploadModal.jsx"; // ✅ bulk upload

// ✅ Import API functions
import { getRisks, createRisk, deleteRisk } from "../../api/riskApi";

const RiskRegisterPage = () => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false); // ✅ bulk modal state

  // Edit Risk modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [viewRisk, setViewRisk] = useState(null);
  const [risks, setRisks] = useState([]);

  // Fetch risks from backend
  const fetchRisks = async () => {
    try {
      const data = await getRisks();
      setRisks(data);
    } catch (error) {
      console.error("Failed to fetch risks:", error);
    }
  };

  useEffect(() => {
    fetchRisks();
  }, []);

  // callback when a new risk is added
  const handleRiskAdded = async (newRiskData) => {
    try {
      
      await fetchRisks();            // then refresh list
      setIsModalOpen(false);         // close modal on success
    } catch (error) {
      console.error("Failed to add risk:", error);
      
    }
 
  };

  
  const handleRiskUpdated = (updatedRisk) => {
  setRisks((prev) =>
    prev.map((r) => (r._id === updatedRisk._id ? updatedRisk : r))
  );
  setIsEditModalOpen(false);
  setSelectedRisk(null);
};


  // Callback when a risk is deleted
  const handleRiskDeleted = async (riskId) => {
    try {
      await deleteRisk(riskId);
      setRisks((prev) => prev.filter((r) => r._id !== riskId));
    } catch (error) {
      console.error("Failed to delete risk:", error);
    }
  };

  // View risk
  const handleViewRisk = (risk) => {
    setViewRisk(risk);
    setIsViewModalOpen(true);
  };

  // 📊 Metrics
  const totalRisks = risks.length;
  const openRisks = risks.filter((r) => r.status === "Open").length;
  const inProgressRisks = risks.filter((r) => r.status === "In Progress").length;
  const closedRisks = risks.filter((r) => r.status === "Closed").length;
  const highImpactRisks = risks.filter((r) => r.impact === "High").length;

  // 📈 Risks Over Time (live)
  const trendData = useMemo(() => {
    const months = [
      "Jan","Feb","Mar","Apr","May","Jun",
      "Jul","Aug","Sep","Oct","Nov","Dec"
    ];
    const counts = Array(12).fill(0);

    risks.forEach((r) => {
      if (!r.createdAt) return;
      const date = new Date(r.createdAt);
      if (date.getFullYear() === new Date().getFullYear()) {
        counts[date.getMonth()] += 1;
      }
    });

    return months.map((month, idx) => ({ month, risks: counts[idx] }));
  }, [risks]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Sidebar */}
      <Sidebar visible={sidebarVisible} onClose={() => setSidebarVisible(false)} />

      {/* Main Content Area */}
      <div className="flex-1 ml-0 md:ml-64 bg-gray-50">
        {/* Topbar */}
        <Topbar onSidebarToggle={() => setSidebarVisible(!sidebarVisible)} />

        {/* Page Content */}
        <div className="p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6 mt-16">
        {/* <div className="p-6 space-y-6 mt-16"> */}
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 text-center sm:text-left">
              Risk Management Dashboard
            </h1>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center justify-center gap-2 px-3 py-2 bg-orange-500 text-white rounded-lg shadow hover:bg-orange-600 transition text-sm sm:text-base"
              >
                <FiPlusCircle /> Add New Risk
              </button>
              <button
                onClick={() => setIsBulkModalOpen(true)} // ✅ open bulk modal
                className="flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg shadow hover:bg-orange-700 transition text-sm sm:text-base"
              >
                <FiPlusCircle /> Bulk Upload
              </button>
            </div>
          </div>

          {/* Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-white shadow rounded-xl p-3 text-center">
          <h2 className="text-gray-500 text-xs sm:text-sm">Total Risks</h2>
          <p className="text-lg sm:text-2xl font-bold">{totalRisks}</p>
        </div>
        <div className="bg-red-100 shadow rounded-xl p-3 text-center">
          <h2 className="text-gray-700 text-xs sm:text-sm">Open Risks</h2>
          <p className="text-lg sm:text-2xl font-bold text-red-700">{openRisks}</p>
        </div>
        <div className="bg-yellow-100 shadow rounded-xl p-3 text-center">
          <h2 className="text-gray-700 text-xs sm:text-sm">In Progress</h2>
          <p className="text-lg sm:text-2xl font-bold text-yellow-700">{inProgressRisks}</p>
        </div>
        <div className="bg-green-100 shadow rounded-xl p-3 text-center">
          <h2 className="text-gray-700 text-xs sm:text-sm">Closed Risks</h2>
          <p className="text-lg sm:text-2xl font-bold text-green-700">{closedRisks}</p>
        </div>
        <div className="bg-indigo-100 shadow rounded-xl p-3 text-center col-span-2 sm:col-span-1">
          <h2 className="text-gray-700 text-xs sm:text-sm">High Impact</h2>
          <p className="text-lg sm:text-2xl font-bold text-indigo-700">{highImpactRisks}</p>
        </div>
      </div>

      {/* Risks Over Time Chart */}
      <div className="w-full h-56 sm:h-72 md:h-96 bg-white shadow rounded-xl p-3 sm:p-4">
        <h2 className="text-base sm:text-lg font-semibold mb-2">Risks Over Time</h2>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="risks" stroke="#10B981" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
           {/* Risk Table */}
          <div className="overflow-x-auto">
            <RiskTable
              risks={risks}
              onEdit={(risk) => {
                setSelectedRisk(risk);
                setIsEditModalOpen(true);
              }}
              onDelete={handleRiskDeleted}
              onView={handleViewRisk}
            />
          </div>
        </div>
      </div>


      {/* Modals */}
      <RiskFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onRiskAdded={handleRiskAdded}
      />

      <EditRiskModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedRisk(null);
        }}
        risk={selectedRisk}
        onRiskUpdated={handleRiskUpdated}
      />

      <ViewRiskModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        risk={viewRisk}
      />

      {isBulkModalOpen && (
  <BulkRiskUploadModal
    closeModal={() => setIsBulkModalOpen(false)}
    onRisksUploaded={fetchRisks}
  />
)}

      {/* <BulkRiskUploadModal
        isOpen={isBulkModalOpen}
        closeModal={() => setIsBulkModalOpen(false)}
        onRisksUploaded={fetchRisks}
      /> */}
    </div>
  );
};

export default RiskRegisterPage;

